import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
  LinearProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  Visibility as Visibility,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import FormSection from './FormSection.jsx';
import { useDispatch } from 'react-redux';
import { downloadLeadFile, downloadAttachment, deleteAttachment } from '../../store/slices/documentsSlice';
import { documentsAPI } from '../../api/documents';
import { validateFile, getFileTypeCategory, formatFileSize } from '../../utils/documents';
import { parseUploadErrorResponse } from '../../utils/errorHandler';
import { toast } from 'react-toastify';
import SweetAlert from '../../utils/sweetAlert.js';

const DocsStep = ({ merchantData, formData = {}, updateFormData, isEditMode = false }) => {
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [attachmentIds, setAttachmentIds] = useState([]); // Store attachment IDs for payload
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fileUploads, setFileUploads] = useState(new Map()); // Track individual file uploads
  const resumableRef = useRef(null);
  const uploadAreaRef = useRef(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // console.log(uploadedFiles, 'faar checking purpose');

  // Resolve DBA/Merchant id for edit mode
  const resolveMerchantId = () => {
    const md = merchantData || {};
    const fromMerchantData = md?.data?.id || md?.id;
    const fromFormData = formData?.dba?.id || formData?.dba_id || formData?.merchant_id || formData?.id;
    return fromMerchantData || fromFormData || null;
  };

  // Application details
  const [applicationAddendum, setApplicationAddendum] = useState(formData.application_addendum || '');
  const [additionalAttachments, setAdditionalAttachments] = useState(formData.additional_attachments || '');
  const isInitialized = useRef(false);

  // Initialize Resumable.js using reusable API
  useEffect(() => {
    const initializeUploader = async () => {
      try {
        // Debug: Check token before initializing
        const token = localStorage.getItem('token');

        const r = await documentsAPI.initializeResumable();
        // If we're editing an existing DBA, include model_id so backend links immediately
        const editMerchantId = resolveMerchantId();
        if (editMerchantId) {
          r.opts.query = { ...(r.opts.query || {}), model_id: editMerchantId };

        }

        // Assign browse button
        if (uploadAreaRef.current) {
          r.assignBrowse(uploadAreaRef.current);
          r.assignDrop(uploadAreaRef.current);
        }

        // Event handlers
        r.on('fileAdded', handleFileAdded);
        r.on('fileProgress', handleFileProgress);
        r.on('fileSuccess', handleFileSuccess);
        r.on('fileError', handleFileError);
        // When all files in the queue have completed (success or error), stop showing loader
        r.on('complete', () => {
          setUploading(false);
        });

        // Additional chunking event handlers
        r.on('chunkingStart', (file) => {
          // console.log('🔄 Chunking started for:', file.fileName);
        });

        r.on('chunkingProgress', (file, ratio) => {
          // console.log('📊 Chunking progress:', file.fileName, `${Math.floor(ratio * 100)}%`);
        });

        r.on('chunkingComplete', (file) => {
          // console.log('✅ Chunking complete for:', file.fileName, `Total chunks: ${file.chunks.length}`);
        });

        r.on('uploadStart', () => {
          // console.log('🚀 Upload started - chunks will be sent to server');
        });

        r.on('error', (message, file) => {
          const errMsg = parseUploadErrorResponse(message, file?.fileName || file?.file?.name);
          toast.error(errMsg, { toastId: 'upload-error' });
        });

        resumableRef.current = r;
      } catch (error) {
        toast.error(error?.message || 'Failed to initialize file uploader');
      }
    };

    initializeUploader();

    return () => {
      if (resumableRef.current) {
        resumableRef.current.cancel();
        // Clean up progress tracking and timeouts
        lastProgressUpdate.current = {};
        Object.values(progressTimeouts.current).forEach(timeout => {
          if (timeout) clearTimeout(timeout);
        });
        progressTimeouts.current = {};
      }
    };
  }, [isEditMode]);

  // Keep model_id in sync if merchant id becomes available later (e.g., after data fetch)
  useEffect(() => {
    const id = resolveMerchantId();
    if (resumableRef.current && id) {
      resumableRef.current.opts.query = {
        ...(resumableRef.current.opts.query || {}),
        model_id: id,
      };
    }
  }, [merchantData, formData]);

  // Update parent formData when docs data changes
  useEffect(() => {
    if (updateFormData) {
      const docsData = {
        documents: uploadedFiles,
        attachments: attachmentIds, // Array of attachment IDs for payload
        application_addendum: applicationAddendum,
        additional_attachments: additionalAttachments
      };

      updateFormData(docsData);



    }
  }, [uploadedFiles, attachmentIds, applicationAddendum, additionalAttachments]);

  // Sync local state with formData prop when navigating between steps
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0 && !isInitialized.current) {
      setUploadedFiles(formData.documents || []);
      setApplicationAddendum(formData.application_addendum || '');
      setAdditionalAttachments(formData.additional_attachments || '');
      isInitialized.current = true;
    }
  }, [formData]);

  // Load existing attachments from formData (same pattern as EquipmentStep)
  useEffect(() => {
    if (!isEditMode) {
      return;
    }


    // Get attachments from formData (same as EquipmentStep uses formData.equipment)
    const existingDocuments = [];
    const existingAttachmentIds = formData.attachments || [];


    if (existingDocuments.length > 0) {
      // Map existing documents to uploadedFiles format
      const files = existingDocuments.map(doc => ({
        id: doc.id,
        document_name: doc.document_name || doc.title || doc.file_name || `attachment-${doc.id}`,
        file_name: doc.file_name || doc.document_name || doc.title || `attachment-${doc.id}`,
        file_size: doc.file_size || 0,
        mime_type: doc.mime_type || doc.type || 'application/octet-stream',
        created_at: doc.created_at || new Date().toISOString(),
        attachment_id: doc.id,
        url: doc.path || doc.file_url // Include URL for download
      }));


      setUploadedFiles(files);
      setAttachmentIds(existingAttachmentIds);

    }
  }, [isEditMode, formData]);

  // Resumable.js event handlers
  const handleFileAdded = (file) => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      const msg = `File validation failed: ${validation.errors.join(', ')}`;
      toast.error(msg, { toastId: 'file-validation' });
      if (resumableRef.current) resumableRef.current.removeFile(file);
      return;
    }

    const fileUpload = {
      id: file.uniqueIdentifier,
      file: file,
      name: file.fileName,
      size: file.size,
      progress: 0,
      status: 'ready',
      error: null,
      type: getFileTypeCategory(file.file?.type || 'application/octet-stream')
    };

    setFileUploads(prev => new Map(prev.set(file.uniqueIdentifier, fileUpload)));
    setUploading(true);

    // toast.info(`File ${file.fileName} added to upload queue`);

    // Auto-start upload immediately when file is added
    setTimeout(() => {
      if (resumableRef.current) {
        resumableRef.current.upload();
        // toast.info('Upload started automatically...');
      } else {
        toast.error('Upload service not ready. Please refresh and try again.');
      }
    }, 100); // Small delay to ensure file is properly added
  };

  // Throttle progress updates to avoid performance issues
  const lastProgressUpdate = useRef({});
  const progressTimeouts = useRef({});

  const getFilePath = (file) =>
    file?.path || file?.url || file?.file_path || file?.file_url;

  const handleFileProgress = (file) => {
    const progress = Math.floor(file.progress() * 100);
    const fileId = file.uniqueIdentifier;

    // Clear any existing timeout for this file
    if (progressTimeouts.current[fileId]) {
      clearTimeout(progressTimeouts.current[fileId]);
    }

    // Debounce progress updates to reduce frequency
    progressTimeouts.current[fileId] = setTimeout(() => {
      const now = Date.now();
      const lastUpdate = lastProgressUpdate.current[fileId];

      // Only update if significant progress change (5% or more) or time elapsed (1 second)
      if (!lastUpdate || (now - lastUpdate > 1000) || (progress - (lastProgressUpdate.current[fileId + '_progress'] || 0) >= 5)) {
        lastProgressUpdate.current[fileId] = now;
        lastProgressUpdate.current[fileId + '_progress'] = progress;

        setFileUploads(prev => {
          const newMap = new Map(prev);
          const fileUpload = newMap.get(fileId);
          if (fileUpload) {
            newMap.set(fileId, {
              ...fileUpload,
              progress,
              status: 'uploading'
            });
          }
          return newMap;
        });
      }
    }, 200); // 200ms debounce
  };

  const handleDownload = async (file) => {
    const filePath = getFilePath(file);
    const attId = file?.id || file?.attachment_id;
    const fileName = file?.document_name || file?.file_name || 'download';

    if (!filePath && !attId) {
      toast.error('No file source available');
      return;
    }

    try {
      setDownloadingId(file.id);

      let blobData; let contentType;
      if (filePath) {
        const res = await dispatch(downloadLeadFile(filePath)).unwrap();
        blobData = res.data; contentType = res.headers?.['content-type'];
      } else {
        blobData = await dispatch(downloadAttachment(attId)).unwrap();
        contentType = file?.mime_type;
      }

      const blob = new Blob([blobData], {
        type: contentType || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success(`${fileName} downloaded`);
    } catch (err) {
      toast.error(err.message ?? 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };
  const handleFileSuccess = (file, message) => {
  try {
    const response = typeof message === 'string' ? JSON.parse(message) : message;

    // Extract attachment ID from response
    const attachmentId = response?.data?.id ||
      response?.id ||
      response?.data?.attachment?.id ||
      response?.attachment?.id ||
      response?.data?.data?.id ||
      response?.attachment_id ||
      response?.data?.attachment_id;

    const filePath = response?.data?.path;
    // console.log(filePath, 'path checking');

    if (attachmentId) {
      setAttachmentIds(prev => {
        const newIds = [...prev, attachmentId];
        return newIds;
      });
    }

    // Remove from active uploads (file is done; it will appear in the table below)
    setFileUploads(prev => {
      const newMap = new Map(prev);
      newMap.delete(file.uniqueIdentifier);
      return newMap;
    });

    // Add to uploaded files
    const newFile = {
      id: attachmentId || file.uniqueIdentifier,
      path: filePath,
      document_name: file.fileName,
      file_name: file.fileName,
      file_size: file.size,
      mime_type: file.file.type || 'application/octet-stream',
      created_at: new Date().toISOString(),
      attachment_id: attachmentId,
      // Store the resumable uniqueIdentifier for reference
      resumableId: file.uniqueIdentifier
    };

    setUploadedFiles(prev => [...prev, newFile]);
  } catch (error) {
    toast.error(error?.message || 'Failed to process upload response');
  }
};

  const handleFileError = (file, message) => {
    const displayMessage = parseUploadErrorResponse(message, file?.fileName || file?.file?.name);
    toast.error(displayMessage, { toastId: 'upload-error' });

    setFileUploads(prev => {
      const newMap = new Map(prev);
      const fileUpload = newMap.get(file.uniqueIdentifier);
      if (fileUpload) {
        newMap.set(file.uniqueIdentifier, {
          ...fileUpload,
          status: 'error',
          error: displayMessage,
        });
      }
      return newMap;
    });

    setUploading(false);
  };

  const handleStartUpload = () => {
    if (resumableRef.current) {
      resumableRef.current.upload();
      toast.info('Starting upload...');
    }
  };

  const handleCancelUpload = () => {
    if (resumableRef.current) {
      resumableRef.current.cancel();
      setFileUploads(new Map());
      setUploading(false);
      toast.info('Upload cancelled');
    }
  };

 const handleRemoveFile = (fileId) => {
  try {
    // Find the file in uploadedFiles to get its resumableId if needed
    const fileToRemove = uploadedFiles.find(file => file.id === fileId);
    
    // Remove from uploadedFiles
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    
    // Remove from attachmentIds
    setAttachmentIds(prev => prev.filter(id => id !== fileId));
    
    // Remove from fileUploads map using multiple possible identifiers
    setFileUploads(prev => {
      const newMap = new Map(prev);
      
      // Try to remove by attachmentId match
      for (let [key, value] of newMap.entries()) {
        if (value.attachmentId === fileId || value.uploadedFileId === fileId || value.id === fileId) {
          newMap.delete(key);
          break;
        }
      }
      
      // If not found by attachmentId, try by resumableId (if we have it from the file)
      if (fileToRemove?.resumableId && newMap.has(fileToRemove.resumableId)) {
        newMap.delete(fileToRemove.resumableId);
      }
      
      return newMap;
    });
    
    toast.success('File removed successfully!');
  } catch (error) {
    toast.error(error?.message || 'Failed to remove file');
  }
};

  const handleConfirmAndDelete = async (file) => {
    try {
      // Show SweetAlert confirmation
      const result = await SweetAlert.deleteConfirm(file.document_name || file.file_name || 'this Attachment');

      // If user cancels, return early
      if (!result.isConfirmed) {
        return;
      }

      await dispatch(deleteAttachment(file?.id)).unwrap();
      handleRemoveFile(file.id);
      await SweetAlert.success({ text: 'Attachment record deleted successfully.' });
    } catch (error) {
      await SweetAlert.error({
        text: error?.message || 'Failed to delete Attachment record'
      });
    }
  };

  const viewFile = (file) => {
    const pathOrId = getFilePath(file) || file?.id || file?.attachment_id;
    if (pathOrId) setPreviewFile(file);
    else toast.error('No file source available for preview');
  };

  const closePreview = () => {
    if (previewBlobUrl) {
      window.URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setPreviewFile(null);
    setPreviewLoading(false);
  };

  // Fetch file for preview (auth required - path is not a direct URL)
  useEffect(() => {
    const pathOrId = previewFile ? getFilePath(previewFile) || previewFile?.id || previewFile?.attachment_id : null;
    if (!pathOrId || !previewFile) {
      setPreviewBlobUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewBlobUrl((prev) => {
      if (prev) window.URL.revokeObjectURL(prev);
      return null;
    });

    const createBlobUrl = (blobData, mimeType) => {
      const blob = new Blob([blobData], {
        type: mimeType || previewFile?.mime_type || 'application/octet-stream',
      });
      return window.URL.createObjectURL(blob);
    };

    const loadPreview = async () => {
      try {
        const path = getFilePath(previewFile);
        if (path) {
          const res = await dispatch(downloadLeadFile(path)).unwrap();
          if (cancelled) return;
          const mime = res.headers?.['content-type'] || previewFile?.mime_type;
          setPreviewBlobUrl(createBlobUrl(res.data, mime));
          return;
        }
        const attId = previewFile.id || previewFile.attachment_id;
        if (attId) {
          const blobData = await dispatch(downloadAttachment(attId)).unwrap();
          if (cancelled) return;
          setPreviewBlobUrl(createBlobUrl(blobData, previewFile?.mime_type));
        }
      } catch {
        if (cancelled) return;
        const attId = previewFile?.id || previewFile?.attachment_id;
        const hadPath = !!getFilePath(previewFile);
        if (hadPath && attId) {
          try {
            const blobData = await dispatch(downloadAttachment(attId)).unwrap();
            if (cancelled) return;
            setPreviewBlobUrl(createBlobUrl(blobData, previewFile?.mime_type));
            return;
          } catch {
            /* fall through */
          }
        }
        toast.error('Failed to load preview');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    loadPreview();
    return () => { cancelled = true; };
  }, [previewFile?.path, previewFile?.url, previewFile?.id, previewFile?.attachment_id, dispatch]);

  const isImagePreview = (file) => {
    const type = (file?.mime_type || file?.type || '').toLowerCase();
    return type.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|svg)(\?|$)/i.test(file?.path || file?.file_name || '');
  };

  const isPdfPreview = (file) => {
    const type = (file?.mime_type || file?.type || '').toLowerCase();
    return type.includes('pdf') || /\.pdf(\?|$)/i.test(file?.path || file?.file_name || '');
  };



  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'uploading':
        return <CircularProgress size={16} />;
      default:
        return <UploadIcon sx={{ color: 'info.main' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      case 'uploading':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Documentation Section */}
      <FormSection title="Documentation" bgColor="blue.50">
        {/* Upload Info */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Files will upload automatically when added. Support for large files with resumable upload. Max 10 files, 256KB chunks for optimal chunking and reliability.
        </Alert>

        {/* File Upload Area */}
        <Card
          ref={uploadAreaRef}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'grey.300',
            bgcolor: dragOver ? 'primary.50' : 'grey.50',
            cursor: uploading ? 'not-allowed' : 'pointer',
            mb: 3,
            opacity: uploading ? 0.6 : 1,
            '&:hover': {
              borderColor: uploading ? 'grey.300' : 'primary.main',
              bgcolor: uploading ? 'grey.50' : 'primary.50'
            },
            transition: 'all 0.2s ease'
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {uploading ? 'Uploading...' : uploadedFiles.length === 0 ? 'No files uploaded' : `${uploadedFiles.length} file(s) uploaded`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {uploading ? 'Please wait while your files are being uploaded' : 'Click to browse or drag files here'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Support for large files with resumable upload
            </Typography>
          </CardContent>
        </Card>

        {/* Upload Controls */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={handleStartUpload}
            disabled={fileUploads.size === 0 || uploading}
          >
            {uploading ? 'Uploading...' : 'Start Upload'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancelUpload}
            disabled={!uploading && fileUploads.size === 0}
          >
            Cancel Upload
          </Button>
        </Box>

        {/* Active Uploads */}
        {fileUploads.size > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Active Uploads ({fileUploads.size})
            </Typography>
            {Array.from(fileUploads.values()).map((fileUpload) => (
              <Card key={fileUpload.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    {getStatusIcon(fileUpload.status)}
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {fileUpload.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(fileUpload.size)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={fileUpload.progress}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {fileUpload.status === 'uploading' ? `Uploading... ${fileUpload.progress}%` :
                        fileUpload.status === 'completed' ? 'Completed ✓' :
                          fileUpload.status === 'error' ? 'Failed ✗' : 'Ready'}
                    </Typography>
                    {fileUpload.error && (
                      <Typography variant="caption" color="error">
                        {fileUpload.error}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Uploaded Files Table */}
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>File Name</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {uploadedFiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No files uploaded yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                uploadedFiles.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>{file.document_name || file.title}</TableCell>
                    <TableCell>{formatFileSize(file.file_size)}</TableCell>
                    <TableCell>
                      <Chip label={file.type || 'Unknown'} size="small" />
                    </TableCell>
                    <TableCell>{new Date(file.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip
                        label="Uploaded"
                        color="success"
                        size="small"
                        icon={<CheckCircleIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => viewFile(file)}
                          title="view"
                        >
                          <Visibility />
                        </IconButton>
                        {(getFilePath(file) || file?.id || file?.attachment_id) && (
                          downloadingId === file.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleDownload(file)}
                              title="Download"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          )
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleConfirmAndDelete(file)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </FormSection>

      {/* Application Details Section */}
      {/* <FormSection title="Application Details" bgColor="grey.50">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Application Addendum"
                value={applicationAddendum}
                onChange={(e) => setApplicationAddendum(e.target.value)}
            placeholder="Enter any additional information for the application..."
          />
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional Attachments"
                value={additionalAttachments}
                onChange={(e) => setAdditionalAttachments(e.target.value)}
            placeholder="Describe any additional attachments or requirements..."
          />
        </Box>
      </FormSection> */}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* File preview popup */}
      <Dialog
        open={!!previewFile}
        onClose={closePreview}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: '95vw',
            maxHeight: '95vh',
            width: 'auto',
            height: 'auto',
            m: 1,
          },
        }}
        BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.8)' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, pr: 1 }}>
          <Typography variant="subtitle1" noWrap sx={{ flex: 1, mr: 2 }}>
            {previewFile?.document_name || previewFile?.file_name || 'Preview'}
          </Typography>
          <IconButton size="small" onClick={closePreview} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 280, minHeight: 200 }}>
          {previewLoading ? (
            <Box sx={{ py: 4 }}>
              <CircularProgress />
            </Box>
          ) : previewBlobUrl && previewFile ? (
            isImagePreview(previewFile) ? (
              <Box component="img" src={previewBlobUrl} alt={previewFile.document_name || previewFile.file_name} sx={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }} />
            ) : isPdfPreview(previewFile) ? (
              <Box component="iframe" src={previewBlobUrl} title={previewFile.document_name} sx={{ width: '80vw', height: '85vh', border: 'none' }} />
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>Preview not available for this file type.</Typography>
                <Button variant="contained" href={previewBlobUrl} download={previewFile.document_name || previewFile.file_name}>
                  Download file
                </Button>
              </Box>
            )
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocsStep;